"use client";

import { useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { InputField } from "@/components/_atoms/form/InputField";
import { Button } from "@/components/_atoms/form/Button";
import {
	SelectField,
	type SelectGroup,
} from "@/components/_atoms/form/SelectField";
import { TextAreaField } from "@/components/_atoms/form/TextAreaField";
import type { ContactRequestPayload } from "@/types/contact";

interface ContactFormValues {
	firstName: string;
	lastName: string;
	email: string;
	subject: string;
	customSubject: string;
	message: string;
	wantsToAddMessage: boolean;
	websiteUrl: string;
}

const BROCHURE_SERVICE_SLUGS = new Set([
	"ameliorer-sa-presence-digitale",
	"maintenance-wordpress",
]);

const AUDIT_SERVICE_SLUG = "audit-site-internet";
const WEBSITE_URL_REGEX = /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i;

const isBrochureSubject = (subject: string) =>
	BROCHURE_SERVICE_SLUGS.has(subject);

const validationSchema = Yup.object({
	firstName: Yup.string().when("subject", {
		is: (value: string) => Boolean(value) && !isBrochureSubject(value),
		then: (schema) => schema.required("Le prénom est requis"),
		otherwise: (schema) => schema.notRequired(),
	}),
	lastName: Yup.string().when("subject", {
		is: (value: string) => Boolean(value) && !isBrochureSubject(value),
		then: (schema) => schema.required("Le nom est requis"),
		otherwise: (schema) => schema.notRequired(),
	}),
	email: Yup.string()
		.matches(
			/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i,
			"Adresse email invalide",
		)
		.when("subject", {
			is: (value: string) => Boolean(value),
			then: (schema) => schema.required("L'e-mail est requis"),
			otherwise: (schema) => schema.notRequired(),
		}),
	subject: Yup.string().required("Le sujet est requis"),
	customSubject: Yup.string().when("subject", {
		is: (value: string) => value === "autre-demande",
		then: (schema) => schema.required("L'objet de la demande est requis"),
		otherwise: (schema) => schema.notRequired(),
	}),
	websiteUrl: Yup.string().when("subject", {
		is: (value: string) => value === AUDIT_SERVICE_SLUG,
		then: (schema) =>
			schema
				.trim()
				.matches(
					WEBSITE_URL_REGEX,
					"L'URL doit commencer par http:// ou https://",
				)
				.required("L'URL du site est requise"),
		otherwise: (schema) => schema.notRequired(),
	}),
	wantsToAddMessage: Yup.boolean(),
	message: Yup.string().when(["subject", "wantsToAddMessage"], {
		is: (subject: string, wantsToAddMessage: boolean) => {
			if (!subject) {
				return false;
			}

			if (isBrochureSubject(subject)) {
				return wantsToAddMessage;
			}

			return true;
		},
		then: (schema) =>
			schema
				.min(10, "Le message doit contenir au moins 10 caractères")
				.required("Le message est requis"),
		otherwise: (schema) => schema.notRequired(),
	}),
});

const createInitialValues = (subject = ""): ContactFormValues => ({
	firstName: "",
	lastName: "",
	email: "",
	subject,
	customSubject: "",
	message: "",
	wantsToAddMessage: false,
	websiteUrl: "",
});

interface ContactApiResponse {
	success?: boolean;
	error?: string;
	datas?: any;
}

interface ContactFormProps {
	serviceOptions: SelectGroup[];
	initialSubject?: string | undefined;
}

export function ContactForm({
	serviceOptions,
	initialSubject,
}: ContactFormProps) {
	const flatOptions = useMemo(
		() => serviceOptions.flatMap((group) => group.options ?? []),
		[serviceOptions],
	);

	const resolvedInitialSubject = useMemo(
		() =>
			initialSubject &&
			flatOptions.some((option) => option.value === initialSubject)
				? initialSubject
				: "",
		[flatOptions, initialSubject],
	);

	const initialValues = useMemo(
		() => createInitialValues(resolvedInitialSubject),
		[resolvedInitialSubject],
	);

	const [submissionStatus, setSubmissionStatus] = useState<
		"idle" | "success" | "error"
	>("idle");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	return (
		<div className="card bg-base-100 border">
			<div className="card-body space-y-6">
				<div>
					<h2 className="card-title text-2xl mb-3">Écrivez-moi</h2>
					<p className="text-base text-muted-foreground">
						Partagez votre projet et vos attentes pour que nous
						puissions construire la meilleure collaboration
						possible.
					</p>
				</div>
				{submissionStatus === "success" ? (
					<div className="space-y-4">
						<h3 className="text-xl font-semibold">
							Merci pour votre message !
						</h3>
						<p className="text-base text-muted-foreground">
							Votre demande a bien été envoyée.
						</p>
						<p className="text-base text-muted-foreground font-semibold">
							Si votre demande concerne la création/refonte de
							site internet ou une brochure : consultez votre
							boîte mail pour passer à l'étape suivante. 🌻
						</p>
						<p className="text-base text-muted-foreground">
							Je reviens vers vous rapidement pour planifier la
							suite.
						</p>
					</div>
				) : (
					<Formik
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={async (
							values,
							{ setSubmitting, resetForm },
						) => {
							setErrorMessage(null);
							setSubmissionStatus("idle");

							const { wantsToAddMessage, ...formValues } = values;
							const selectedOption = flatOptions.find(
								(option) => option.value === formValues.subject,
							);

							const resolvedSubject =
								formValues.subject === "autre-demande"
									? formValues.customSubject
									: (selectedOption?.label ??
										formValues.subject);

							const trimmedSubject = resolvedSubject.trim();
							const trimmedMessage = formValues.message
								? formValues.message.trim()
								: "";
							const payload: ContactRequestPayload = {
								firstName:
									formValues.firstName.trim() || undefined,
								lastName:
									formValues.lastName.trim() || undefined,
								email: formValues.email.trim(),
								subject:
									trimmedSubject ||
									selectedOption?.label ||
									formValues.subject,
								message:
									isBrochureSubject(formValues.subject) &&
									!wantsToAddMessage
										? undefined
										: trimmedMessage
											? trimmedMessage
											: undefined,
								serviceSlug: formValues.subject,
								serviceLabel: selectedOption?.label,
								websiteUrl:
									formValues.subject === AUDIT_SERVICE_SLUG
										? formValues.websiteUrl.trim() ||
											undefined
										: undefined,
							};

							try {
								const response = await fetch("/api/contact", {
									method: "POST",
									headers: {
										"Content-Type": "application/json",
									},
									body: JSON.stringify(payload),
								});

								const result: ContactApiResponse =
									await response.json();

								if (!response.ok || !result.success) {
									throw new Error(
										result.error ?? "Erreur inconnue",
									);
								}
								setSubmissionStatus("success");
								resetForm();
							} catch (error) {
								setSubmissionStatus("error");
								setErrorMessage(
									error instanceof Error
										? error.message
										: "Une erreur inattendue est survenue.",
								);
							} finally {
								setSubmitting(false);
							}
						}}
						enableReinitialize
					>
						{({
							resetForm,
							isSubmitting,
							values,
							setFieldValue,
						}) => (
							<Form className="space-y-6">
								<div className="mb-5">
									<SelectField
										name="subject"
										label="Sujet"
										placeholder="Choisissez un service"
										options={serviceOptions}
										onValueChange={(value) => {
											setErrorMessage(null);
											setSubmissionStatus("idle");

											if (value !== "autre-demande") {
												setFieldValue(
													"customSubject",
													"",
												);
											}
											if (value !== AUDIT_SERVICE_SLUG) {
												setFieldValue("websiteUrl", "");
											}
											setFieldValue(
												"wantsToAddMessage",
												false,
											);

											if (isBrochureSubject(value)) {
												setFieldValue("message", "");
											}
										}}
									/>
								</div>
								{values.subject ? (
									isBrochureSubject(values.subject) ? (
										<div className="space-y-4">
											<p className="text-base text-muted-foreground">
												Vous recevrez la brochure de
												présentation ainsi que le tarif
												par e-mail.
											</p>
											<InputField
												name="email"
												type="email"
												label="Adresse e-mail"
												placeholder="vous@example.com"
												autoComplete="email"
											/>
											<div className="form-control my-4">
												<label className="label cursor-pointer justify-start gap-3">
													<input
														id="wantsToAddMessage"
														name="wantsToAddMessage"
														type="checkbox"
														className="checkbox not-checked:border border-[var(--border)] checkbox-primary"
														checked={
															values.wantsToAddMessage
														}
														onChange={(event) => {
															setFieldValue(
																"wantsToAddMessage",
																event.target
																	.checked,
															);

															if (
																!event.target
																	.checked
															) {
																setFieldValue(
																	"message",
																	"",
																);
															}
														}}
													/>
													<span className="label-text text-base">
														Je veux ajouter un
														message
													</span>
												</label>
											</div>
											{values.wantsToAddMessage ? (
												<TextAreaField
													name="message"
													label="Message"
													placeholder="Décrivez votre projet, vos objectifs et vos contraintes..."
													rows={6}
												/>
											) : null}
										</div>
									) : (
										<div className="space-y-4">
											<ul className="grid gap-4 md:grid-cols-2">
												<li>
													<InputField
														name="firstName"
														label="Prénom"
														placeholder="Votre prénom"
														autoComplete="given-name"
													/>
												</li>
												<li>
													<InputField
														name="lastName"
														label="Nom"
														placeholder="Votre nom"
														autoComplete="family-name"
													/>
												</li>
											</ul>
											<ul className="grid gap-4">
												<li>
													<InputField
														name="email"
														type="email"
														label="Adresse e-mail"
														placeholder="vous@example.com"
														autoComplete="email"
													/>
												</li>

												{values.subject ===
												AUDIT_SERVICE_SLUG ? (
													<li>
														<InputField
															name="websiteUrl"
															type="url"
															label="URL du site à auditer"
															placeholder="https://votre-site.fr"
															autoComplete="url"
														/>
													</li>
												) : null}

												{values.subject ===
												"autre-demande" ? (
													<li>
														<InputField
															name="customSubject"
															label="Objet de la demande"
															placeholder="Précisez l'objet de votre demande"
														/>
													</li>
												) : null}
												<li>
													<TextAreaField
														name="message"
														label="Message"
														placeholder="Décrivez votre projet, vos objectifs et vos contraintes..."
														rows={6}
													/>
												</li>
											</ul>
										</div>
									)
								) : null}
								<div className="flex flex-wrap gap-3">
									<Button
										type="submit"
										buttonStyle="btn-neutral"
										isLoading={isSubmitting}
										disabled={isSubmitting}
									>
										{isBrochureSubject(values.subject)
											? "Recevoir la brochure"
											: "Envoyer ma demande"}
									</Button>
									<Button
										type="button"
										buttonStyle="btn-outline border-1"
										onClick={() => {
											resetForm();
											setErrorMessage(null);
											setSubmissionStatus("idle");
										}}
										disabled={isSubmitting}
									>
										Réinitialiser le formulaire
									</Button>
								</div>
								{errorMessage ? (
									<p
										role="alert"
										className="text-sm text-error"
										aria-live="polite"
									>
										{errorMessage}
									</p>
								) : null}
							</Form>
						)}
					</Formik>
				)}
			</div>
		</div>
	);
}
